import { type ReactElement } from 'react';
import { Box, FeatureErrorBoundary, Text } from '@citibox-panel/ui';
import type { ExclusionListCarrier, ExclusionListSeams } from '../lib/excludedUser';
import { AddUserCard } from './AddUserCard';
import { ExcludedUsersCard } from './ExcludedUsersCard';
import { ExclusionSummary } from './ExclusionSummary';
import { neverHandledByHost, type IsHandledByHost } from '../../../lib/hostErrors';

export interface CarrierExclusionSectionProps extends ExclusionListSeams {
  carrier: ExclusionListCarrier;
  timeZone: string | null;
  // Kept apart from the data seams: it is no carrier's data, just which errors the host already shows.
  isHandledByHost?: IsHandledByHost;
}

// One carrier's block: its name, what the list means, and the two cards. The page stacks one per
// catalog entry, so a second carrier costs a catalog line and nothing here. Each card sits in its
// own boundary: a card that throws takes itself and nothing else.
export function CarrierExclusionSection({
  carrier,
  timeZone,
  fetchExcludedUsers,
  addExcludedUser,
  removeExcludedUser,
  isHandledByHost = neverHandledByHost,
}: CarrierExclusionSectionProps): ReactElement {
  return (
    <Box component="section" aria-label={carrier.name} sx={{ mb: 4 }}>
      <Text variant="h6" component="h2" bold>
        {carrier.name}
      </Text>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          gap: 3,
          alignItems: 'start',
          mt: 2,
        }}
      >
        <Box>
          {/* Static copy has nothing to throw, and a card that does must not take the words that
              say what the block is for. */}
          <ExclusionSummary carrierName={carrier.name} />
          <FeatureErrorBoundary>
            <AddUserCard
              carrier={carrier}
              addExcludedUser={addExcludedUser}
              isHandledByHost={isHandledByHost}
            />
          </FeatureErrorBoundary>
        </Box>
        <FeatureErrorBoundary>
          <ExcludedUsersCard
            carrier={carrier}
            timeZone={timeZone}
            fetchExcludedUsers={fetchExcludedUsers}
            removeExcludedUser={removeExcludedUser}
            isHandledByHost={isHandledByHost}
          />
        </FeatureErrorBoundary>
      </Box>
    </Box>
  );
}
